import { useState, useEffect } from 'react';
import { repairOrdersService, RepairOrder, RepairOrderWithItems, RepairOrderFormData, StockValidationResult } from '../services/repairOrdersService';
import { useAuth } from '../context/AuthContext';

export const useRepairOrders = () => {
  const { businessId } = useAuth();
  const [orders, setOrders] = useState<RepairOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null); // ID of order being updated
  const [deleting, setDeleting] = useState<string | null>(null); // ID of order being deleted

  const fetchRepairOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const { orders: data, error: fetchError } = await repairOrdersService.getRepairOrders(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch repair orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchRepairOrders();
    }
  }, [businessId]);

  const refreshRepairOrders = () => {
    fetchRepairOrders();
  };

  const createRepairOrder = async (orderData: RepairOrderFormData) => {
    try {
      setCreating(true);
      setError(null);

      const { order, error: createError } = await repairOrdersService.createRepairOrder(orderData, businessId || undefined);

      if (createError) {
        throw createError;
      }

      if (order) {
        setOrders(prev => [order, ...prev]);
      }

      return { order, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create repair order');
      setError(error.message);
      return { order: null, error };
    } finally {
      setCreating(false);
    }
  };

  const updateRepairOrder = async (id: string, orderData: RepairOrderFormData) => {
    try {
      setUpdating(id);
      setError(null);

      const { order, error: updateError } = await repairOrdersService.updateRepairOrder(id, orderData, businessId || undefined);

      if (updateError) {
        throw updateError;
      }

      if (order) {
        setOrders(prev => prev.map(o => o.id === id ? order : o));
      }

      return { order, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update repair order');
      setError(error.message);
      return { order: null, error };
    } finally {
      setUpdating(null);
    }
  };

  const deleteRepairOrder = async (id: string) => {
    try {
      setDeleting(id);
      setError(null);

      const { error: deleteError } = await repairOrdersService.deleteRepairOrder(id, businessId || undefined);

      if (deleteError) {
        throw deleteError;
      }

      setOrders(prev => prev.filter(order => order.id !== id));
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete repair order');
      setError(error.message);
      return { error };
    } finally {
      setDeleting(null);
    }
  };

  const updateRepairOrderStatus = async (id: string, status: 'pending' | 'process' | 'completed') => {
    try {
      setUpdating(id);
      setError(null);

      const { order, error: updateError } = await repairOrdersService.updateRepairOrderStatus(id, status, businessId || undefined);

      if (updateError) {
        throw updateError;
      }

      if (order) {
        setOrders(prev => prev.map(o => o.id === id ? order : o));
      }

      return { order, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update repair order status');
      setError(error.message);
      return { order: null, error };
    } finally {
      setUpdating(null);
    }
  };

  const validateStock = async (items: RepairOrderFormData['items']): Promise<{ results: StockValidationResult[]; error: Error | null }> => {
    return await repairOrdersService.validateStock(items);
  };

  return {
    orders,
    loading,
    error,
    creating,
    updating,
    deleting,
    refreshRepairOrders,
    createRepairOrder,
    updateRepairOrder,
    deleteRepairOrder,
    updateRepairOrderStatus,
    validateStock,
  };
};

export const useRepairOrder = (id: string | null) => {
  const { businessId } = useAuth();
  const [order, setOrder] = useState<RepairOrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const { order: data, error: fetchError } = await repairOrdersService.getRepairOrderById(id, businessId || undefined);

        if (fetchError) {
          throw fetchError;
        }

        setOrder(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch repair order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, businessId]);

  return {
    order,
    loading,
    error,
  };
};